import { Injectable } from '@angular/core';
import {
	Export,
	MultiChoiceGameData,
	PluginType,
} from '@meta-press/shared-plugins';
import { TextArticle } from '@meta-press/shared-plugins';
import { ImageArticle } from '@meta-press/shared-plugins';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as pdfjs from 'pdfjs-dist';
import { PDFPageProxy } from 'pdfjs-dist/types/src/display/api';

@Injectable({
	providedIn: 'root',
})
export class PDFService {
	// Track whether we've already fallen back to CDN to prevent infinite loops
	private hasTriedCdn = false;
	private isUsingCdn = false;

	// TEMPORARY TESTING FLAG: Set to true to force CDN fallback for testing
	// TODO: Remove this after testing is complete
	private FORCE_CDN_FALLBACK = false;

	constructor() {
		this.initWorker();
	}

	private initWorker(): void {
		try {
			// TEMPORARY: Force immediate CDN fallback for testing
			if (this.FORCE_CDN_FALLBACK) {
				console.warn('🧪 TESTING MODE: Forcing CDN fallback instead of using local worker');
				this.fallbackToCdn();
				return;
			}

			// Get base URL from the HTML base tag to ensure correct path resolution
			const baseUrl = document.querySelector('base')?.getAttribute('href') || '/';
			// Worker is in assets folder, use relative path from base
			pdfjs.GlobalWorkerOptions.workerSrc = `${baseUrl}assets/pdfjs-dist/pdf.worker.min.mjs`;
			console.log('PDF.js worker initialized with local assets path:', pdfjs.GlobalWorkerOptions.workerSrc);
		} catch (error) {
			console.error('Failed to initialize PDF.js worker with assets path:', error);
			this.fallbackToCdn();
		}
	}

	/**
	 * Switch to CDN-hosted worker when local worker fails
	 */
	private fallbackToCdn(): void {
		if (this.hasTriedCdn) {
			console.warn('Already attempted CDN fallback, skipping to prevent loop');
			return;
		}

		try {
			this.hasTriedCdn = true;
			this.isUsingCdn = true;
			// Use CDN worker which has correct MIME types
			pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
			console.log('PDF.js worker switched to CDN fallback');
		} catch (fallbackError) {
			console.error('Failed to initialize PDF.js worker with CDN fallback:', fallbackError);
		}
	}

	/**
	 * Wraps PDF.js getDocument to handle worker initialization errors
	 * and automatically fall back to CDN if needed
	 */
	private async getDocumentWithFallback(data: any): Promise<any> {
		try {
			const loadingTask = pdfjs.getDocument(data);
			const pdf = await loadingTask.promise;
			return pdf;
		} catch (error: any) {
			// Check if error is related to worker loading/MIME type issues
			const errorMessage = error?.message || '';
			if (
				!this.isUsingCdn &&
				(errorMessage.includes('fake worker') ||
				errorMessage.includes('Failed to fetch dynamically imported module') ||
				errorMessage.includes('MIME type') ||
				errorMessage.includes('worker'))
			) {
				console.warn('PDF worker failed to load, attempting CDN fallback:', errorMessage);
				this.fallbackToCdn();

				// Retry with CDN worker
				const loadingTask = pdfjs.getDocument(data);
				const pdf = await loadingTask.promise;
				return pdf;
			}

			// Re-throw if not a worker-related error or already using CDN
			throw error;
		}
	}

	/**
	 * Extract raw image data from PDF file for user selection with progressive callback
	 * @param file PDF file to extract images from
	 * @param onImageFound Callback function called each time an image is found
	 * @param onProgress Callback function called with progress updates
	 * @param abortSignal Optional AbortSignal to cancel the extraction process
	 * @returns Promise with array of raw image data
	 */
	public async extractRawImagesFromPDF(
		file: File,
		onImageFound?: (image: {dataUrl: string; width: number; height: number; pageNumber: number}) => void,
		onProgress?: (progress: {currentPage: number; totalPages: number; currentPageImages: number}) => void,
		abortSignal?: AbortSignal
	): Promise<Array<{
		dataUrl: string;
		width: number;
		height: number;
		pageNumber: number;
	}>> {
		try {
			const arrayBuffer = await this.readFileAsArrayBuffer(file);
			const pdf = await this.getDocumentWithFallback({ data: arrayBuffer });

			const allImages: Array<{
				dataUrl: string;
				width: number;
				height: number;
				pageNumber: number;
			}> = [];

			// Process each page
			for (let i = 1; i <= pdf.numPages; i++) {
				// Check for cancellation before processing each page
				if (abortSignal?.aborted) {
					console.log('PDF extraction cancelled by user');
					throw new Error('PDF extraction cancelled');
				}

				console.log(`Starting extraction for page ${i}`);
				const page = await pdf.getPage(i);

				// Update progress
				onProgress?.({
					currentPage: i,
					totalPages: pdf.numPages,
					currentPageImages: 0
				});

				const images = await this.extractImagesFromPage(page, (image) => {
					// Check for cancellation during image processing
					if (abortSignal?.aborted) {
						return; // Stop processing images if cancelled
					}

					const imageWithPage = { ...image, pageNumber: i };
					allImages.push(imageWithPage);
					onImageFound?.(imageWithPage);

					// Update progress with current image count (use allImages length for now)
					onProgress?.({
						currentPage: i,
						totalPages: pdf.numPages,
						currentPageImages: allImages.filter(img => img.pageNumber === i).length
					});
				}, abortSignal);

				console.log(`Page ${i} extraction completed, found ${images.length} images`);
			}

			console.log(`All pages processed. Total images: ${allImages.length}`);
			return allImages;
		} catch (error) {
			console.error('Error extracting raw images from PDF:', error);
			throw error;
		}
	}

	/**
	 * Extract content from PDF file and return as TextArticle and ImageArticle objects
	 * @param file PDF file to extract content from
	 * @param creator Name of the creator
	 * @param includeImages Whether to extract images from PDF (default: false)
	 * @returns Promise with arrays of TextArticle and ImageArticle objects
	 */
	public async extractContentFromPDF(
		file: File,
		creator: string,
		includeImages = false,
	): Promise<{
		textArticles: TextArticle[];
		imageArticles: ImageArticle[];
	}> {
		try {
			const arrayBuffer = await this.readFileAsArrayBuffer(file);
			const pdf = await this.getDocumentWithFallback({ data: arrayBuffer });

			const textArticles: TextArticle[] = [];
			const imageArticles: ImageArticle[] = [];


			// Process each page
			for (let i = 1; i <= pdf.numPages; i++) {
				const page = await pdf.getPage(i);

				// Extract text
				const textContent = await page.getTextContent();
				if (textContent.items.length > 0) {
					const extractedText = textContent.items
						.map((item: any) => item.str)
						.join(' ');

					if (extractedText.trim()) {
						const textArticle = new TextArticle(
							`Page ${i} Text from ${file.name}`,
							creator,
							0,
							extractedText,
							Date.now() + i,
							[],
							[],
						);
						textArticles.push(textArticle);
					}
				}

				// Extract images only if requested
				if (includeImages) {
					const images = await this.extractImagesFromPage(page);
					for (let j = 0; j < images.length; j++) {
						const image = images[j];
						const imageArticle = new ImageArticle(
							`Image ${j + 1} from page ${i} of ${file.name}`,
							creator,
							`${image.width}x${image.height}`,
							0,
							{ type: 'url', url: image.dataUrl },
							Date.now() + i * 1000 + j,
							[],
							[],
							{ pageNumber: i },
						);
						imageArticles.push(imageArticle);
					}
				}
			}

			return { textArticles, imageArticles };
		} catch (error) {
			console.error('Error extracting content from PDF:', error);
			throw error;
		}
	}

	/**
	 * Extract text from PDF file for validation purposes
	 * Checks first few pages to determine if PDF contains readable text
	 * @param file PDF file to extract text from
	 * @returns Promise with extracted text string
	 */
	public async extractTextFromPDF(file: File): Promise<string> {
		try {
			const arrayBuffer = await this.readFileAsArrayBuffer(file);
			const pdf = await this.getDocumentWithFallback({ data: arrayBuffer });

			let allText = '';

			// Extract text from all pages (limit to first 3 pages for performance)
			const pagesToCheck = Math.min(pdf.numPages, 3);

			for (let i = 1; i <= pagesToCheck; i++) {
				const page = await pdf.getPage(i);
				const textContent = await page.getTextContent();

				const pageText = textContent.items
					.map((item: any) => item.str)
					.join(' ');

				allText += pageText + '\n';
			}

			return allText.trim();
		} catch (error) {
			console.error('Error extracting text from PDF:', error);
			throw error;
		}
	}

	/**
	 * Extract images from a PDF page with progressive callback
	 * @param page PDF page to extract images from
	 * @param onImageFound Optional callback called when each image is found
	 * @param abortSignal Optional AbortSignal to cancel the extraction process
	 * @returns Promise with array of image data
	 */
	private async extractImagesFromPage(
		page: PDFPageProxy,
		onImageFound?: (image: {dataUrl: string; width: number; height: number}) => void,
		abortSignal?: AbortSignal
	): Promise<Array<{
		dataUrl: string;
		width: number;
		height: number;
	}>> {
		console.log('Starting extractImagesFromPage');
		const operatorList = await page.getOperatorList();
		console.log('Got operator list');
		const imageObjs: Array<{
			dataUrl: string;
			width: number;
			height: number;
		}> = [];

		// Create a canvas to render each image
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');

		if (!ctx) {
			throw new Error('Could not get canvas context');
		}

		const imgData = [];

		// Process all operations looking for image data
		for (let i = 0; i < operatorList.fnArray.length; i++) {
			if (
				operatorList.fnArray[i] === pdfjs.OPS.paintXObject ||
				operatorList.fnArray[i] === pdfjs.OPS.paintImageXObject
			) {
				const imgIndex = operatorList.argsArray[i][0];
				imgData.push(imgIndex);
			}
		}

		console.log(`Found ${imgData.length} image operations`);

		// Get actual image data using callback pattern with timeout and cancellation support
		for (const imgIdx of imgData) {
			// Check for cancellation before processing each image
			if (abortSignal?.aborted) {
				console.log('Image extraction cancelled during processing');
				break;
			}

			try {
				console.log(`Processing image: ${imgIdx}`);

				// Use Promise.race to add timeout and cancellation support
				await Promise.race([
					new Promise<void>((resolve) => {
						page.objs.get(imgIdx, (img: any) => {
							// Check for cancellation in callback
							if (abortSignal?.aborted) {
								console.log(`Image extraction cancelled for ${imgIdx}`);
								resolve();
								return;
							}

							console.log(`Callback received for ${imgIdx}:`, !!img);
							if (!img) {
								console.log(`Could not resolve object: ${imgIdx}`);
								resolve();
								return;
							}

							try {
								const result = this.processImageObject(img, imgIdx, canvas, ctx);
								if (result && !abortSignal?.aborted) {
									imageObjs.push(result);
									// Call the progressive callback immediately when image is found
									onImageFound?.(result);
								}
							} catch (error) {
								console.error(`Error processing image ${imgIdx}:`, error);
							}
							resolve();
						});
					}),
					new Promise<void>((resolve) => {
						setTimeout(() => {
							console.log(`Timeout reached for image ${imgIdx}, skipping`);
							resolve();
						}, 5000); // 5 second timeout
					}),
					// Add cancellation promise
					new Promise<void>((resolve) => {
						if (abortSignal) {
							abortSignal.addEventListener('abort', () => {
								console.log(`Abort signal received for image ${imgIdx}`);
								resolve();
							});
						}
					})
				]);
				console.log(`Completed processing image: ${imgIdx}`);
			} catch (err) {
				console.error('Error processing image:', err);
			}
		}

		console.log(`extractImagesFromPage completed with ${imageObjs.length} images`);
		return imageObjs;
	}

	/**
	 * Helper function to render pixel data to canvas, handling various formats (RGBA, RGB, Grayscale)
	 * @param pixelData The pixel data as Uint8ClampedArray
	 * @param width Image width
	 * @param height Image height
	 * @param ctx Canvas rendering context
	 * @param imgIdx Image index for logging
	 * @returns true if successful, false otherwise
	 */
	private renderPixelDataToCanvas(
		pixelData: Uint8ClampedArray,
		width: number,
		height: number,
		ctx: CanvasRenderingContext2D,
		imgIdx: string
	): boolean {
		try {
			// Try RGBA format (4 bytes per pixel)
			if (pixelData.length === width * height * 4) {
				const imageData = new ImageData(pixelData, width, height);
				ctx.putImageData(imageData, 0, 0);
				console.log(`Successfully processed ${imgIdx} as RGBA (${pixelData.length} bytes)`);
				return true;
			}
			// Try RGB format (3 bytes per pixel) - convert to RGBA
			else if (pixelData.length === width * height * 3) {
				const rgbaData = new Uint8ClampedArray(width * height * 4);
				for (let i = 0; i < pixelData.length; i += 3) {
					const rgbaIndex = (i / 3) * 4;
					rgbaData[rgbaIndex] = pixelData[i];     // R
					rgbaData[rgbaIndex + 1] = pixelData[i + 1]; // G
					rgbaData[rgbaIndex + 2] = pixelData[i + 2]; // B
					rgbaData[rgbaIndex + 3] = 255;         // A (fully opaque)
				}
				const imageData = new ImageData(rgbaData, width, height);
				ctx.putImageData(imageData, 0, 0);
				console.log(`Successfully converted ${imgIdx} from RGB to RGBA (${pixelData.length} bytes)`);
				return true;
			}
			// Try Grayscale format (1 byte per pixel) - convert to RGBA
			else if (pixelData.length === width * height) {
				const rgbaData = new Uint8ClampedArray(width * height * 4);
				for (let i = 0; i < pixelData.length; i++) {
					const rgbaIndex = i * 4;
					const gray = pixelData[i];
					rgbaData[rgbaIndex] = gray;     // R
					rgbaData[rgbaIndex + 1] = gray; // G
					rgbaData[rgbaIndex + 2] = gray; // B
					rgbaData[rgbaIndex + 3] = 255;  // A (fully opaque)
				}
				const imageData = new ImageData(rgbaData, width, height);
				ctx.putImageData(imageData, 0, 0);
				console.log(`Successfully converted ${imgIdx} from Grayscale to RGBA (${pixelData.length} bytes)`);
				return true;
			}
			// Unknown format
			else {
				console.warn(`Unknown pixel format for ${imgIdx}: expected ${width * height} (grayscale), ${width * height * 3} (RGB), or ${width * height * 4} (RGBA) bytes, got ${pixelData.length}`);
				return false;
			}
		} catch (error) {
			console.error(`Error rendering pixel data for ${imgIdx}:`, error);
			return false;
		}
	}

	/**
	 * Helper function to extract pixel data from various data formats
	 * @param data The data object that might contain pixel information
	 * @returns Uint8ClampedArray if successful, null otherwise
	 */
	private extractPixelData(data: any): Uint8ClampedArray | null {
		// Already a Uint8ClampedArray
		if (data instanceof Uint8ClampedArray) {
			return data;
		}

		// Uint8Array - convert to Uint8ClampedArray
		if (data instanceof Uint8Array) {
			return new Uint8ClampedArray(data);
		}

		// ArrayBuffer - convert to Uint8ClampedArray
		if (data instanceof ArrayBuffer) {
			return new Uint8ClampedArray(data);
		}

		// Any ArrayBufferView (Int8Array, Uint16Array, etc.)
		if (ArrayBuffer.isView(data)) {
			return new Uint8ClampedArray(data.buffer, data.byteOffset, data.byteLength);
		}

		// Plain object that might have a buffer, bytes, or data property
		if (data && typeof data === 'object') {
			// Try common property names
			const candidates = ['buffer', 'bytes', 'data', '_data'];
			for (const prop of candidates) {
				if (prop in data && data[prop]) {
					const extracted = this.extractPixelData(data[prop]);
					if (extracted) {
						return extracted;
					}
				}
			}

			// Try to convert object values to array if it's array-like
			if ('length' in data && typeof data.length === 'number') {
				try {
					const arr = Array.from(data);
					if (arr.every((val: any) => typeof val === 'number')) {
						return new Uint8ClampedArray(arr);
					}
				} catch (e) {
					// Not array-like, continue
				}
			}
		}

		return null;
	}

	/**
	 * Process a single image object and return the result if successful
	 * @param img The image object from PDF.js
	 * @param imgIdx The image index for logging
	 * @param canvas Canvas element for image processing
	 * @param ctx Canvas 2D context
	 * @returns Image data object if successful, null otherwise
	 */
	private processImageObject(
		img: any,
		imgIdx: string,
		canvas: HTMLCanvasElement,
		ctx: CanvasRenderingContext2D
	): { dataUrl: string; width: number; height: number } | null {
		// Debug: Log detailed image object structure with enhanced bitmap/data inspection
		console.log(`Processing image ${imgIdx}:`, {
			type: typeof img,
			constructor: img?.constructor?.name,
			properties: img ? Object.keys(img).slice(0, 10) : [], // Limit to first 10 properties
			hasWidth: 'width' in img,
			hasHeight: 'height' in img,
			hasData: 'data' in img,
			hasBitmap: 'bitmap' in img,
			width: img?.width,
			height: img?.height,
			dataType: img?.data ? typeof img.data : 'none',
			dataConstructor: img?.data?.constructor?.name,
			dataLength: img?.data?.length,
			dataIsArrayBuffer: img?.data instanceof ArrayBuffer,
			dataIsArrayBufferView: img?.data ? ArrayBuffer.isView(img?.data) : false,
			dataKeys: img?.data && typeof img.data === 'object' ? Object.keys(img.data).slice(0, 5) : [],
			bitmapType: img?.bitmap ? typeof img.bitmap : 'none',
			bitmapConstructor: img?.bitmap?.constructor?.name,
			bitmapKeys: img?.bitmap && typeof img.bitmap === 'object' ? Object.keys(img.bitmap).slice(0, 5) : [],
			bitmapHasData: img?.bitmap && 'data' in img.bitmap,
			bitmapDataType: img?.bitmap?.data ? typeof img.bitmap.data : 'none'
		});

		let width = 0;
		let height = 0;

		// Extract dimensions
		if ('width' in img && 'height' in img) {
			width = img.width;
			height = img.height;
		}

		if (width <= 0 || height <= 0) {
			console.log(`Image ${imgIdx} has invalid dimensions: ${width}x${height}`);
			return null;
		}

		// Set canvas dimensions
		canvas.width = width;
		canvas.height = height;
		ctx.clearRect(0, 0, width, height);

		let success = false;

		// Try different approaches to extract the image
		if (img instanceof ImageBitmap || img instanceof HTMLImageElement) {
			// Direct drawable images
			console.log(`Drawing ${img.constructor.name} directly to canvas`);
			ctx.drawImage(img, 0, 0);
			success = true;
		}
		// Handle PDF.js image objects with bitmap property
		else if (img.bitmap) {
			console.log(`Attempting to process bitmap property for ${imgIdx}`);
			try {
				if (img.bitmap instanceof ImageBitmap || img.bitmap instanceof HTMLImageElement) {
					ctx.drawImage(img.bitmap, 0, 0);
					success = true;
					console.log(`Successfully drew bitmap as ${img.bitmap.constructor.name}`);
				} else {
					// Try to extract pixel data from bitmap object using helper function
					const pixelData = this.extractPixelData(img.bitmap);

					if (pixelData) {
						success = this.renderPixelDataToCanvas(pixelData, width, height, ctx, imgIdx);
					}
				}
			} catch (error) {
				console.error(`Error processing bitmap for image ${imgIdx}:`, error);
			}
		}
		// Handle direct data property - now handles any format using helper function
		else if (img.data) {
			console.log(`Processing data property for ${imgIdx}`);
			try {
				const pixelData = this.extractPixelData(img.data);
				if (pixelData) {
					success = this.renderPixelDataToCanvas(pixelData, width, height, ctx, imgIdx);
				} else {
					console.log(`Could not extract pixel data from img.data for ${imgIdx}`);
				}
			} catch (error) {
				console.error(`Error processing data property for image ${imgIdx}:`, error);
			}
		}

		if (success) {
			try {
				const dataUrl = canvas.toDataURL('image/png');
				// Validate data URL is not empty
				if (dataUrl && dataUrl.length > 100) {
					console.log(`Successfully extracted embedded image ${imgIdx}: ${width}x${height}`);
					return { dataUrl, width, height };
				}
			} catch (error) {
				console.error(`Error generating data URL for image ${imgIdx}:`, error);
			}
		} else {
			console.log(`Could not extract embedded image ${imgIdx} - unsupported format. Available properties:`, Object.keys(img));
		}

		return null;
	}

	/**
	 * Read a file as ArrayBuffer
	 * @param file File to read
	 * @returns Promise with ArrayBuffer
	 */
	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				resolve(reader.result as ArrayBuffer);
			};
			reader.onerror = reject;
			reader.readAsArrayBuffer(file);
		});
	}

	/**
	 * Generate a PDF from an export object
	 * @param exportData The export data to convert to PDF
	 * @returns Promise that resolves when PDF generation is complete
	 */
	public generatePDFFromExport(exportData: Export): Promise<Blob> {
		return new Promise((resolve, reject) => {
			try {
				const doc = new jsPDF('p', 'mm', 'a4');

				// Set document metadata
				doc.setProperties({
					title: exportData.name || 'Meta Press Export',
					author: exportData.creator || 'Unknown Author',
					creator: 'Meta Press PDF Service',
				});

				// Add title
				doc.setFontSize(20);
				doc.text(exportData.name || 'Untitled Export', 20, 20);

				// Add creation info
				doc.setFontSize(10);
				doc.text(
					`Created by: ${exportData.creator || 'Unknown Author'}`,
					20,
					30,
				);
				doc.text(
					`Date: ${exportData.date || new Date().toLocaleString()}`,
					20,
					35,
				);

				// Add content based on game data if available
				if (exportData.gameData) {
					this.addGameDataToPDF(doc, exportData);
				} else {
					doc.text('No game data available', 20, 45);
				}

				// Convert to blob and return
				const pdfBlob = doc.output('blob');
				resolve(pdfBlob);
			} catch (error) {
				reject(error);
			}
		});
	}

	/**
	 * Add game data to the PDF document
	 * @param doc The PDF document
	 * @param exportData The export data containing game data
	 */
	private addGameDataToPDF(doc: jsPDF, exportData: Export): void {
		const yPosition = 45;

		if (exportData.gameData) {
			switch (exportData.gameData.type) {
				case PluginType.MultiChoice:
					GenerateMultiChoicePDF(exportData, doc, yPosition);
					break;
				default:
					throw new Error(
						`Pelityypin PDF-vientiä ei ole vielä määritelty: ${PluginType[exportData.gameData.type]}`,
					);
			}
		} else {
			throw new Error(`Export on tyhjä. Ei pelidataa saatavilla.`);
		}
	}

	/**
	 * Save PDF to file with the given filename
	 * @param pdfBlob The PDF blob to save
	 * @param filename The filename to use
	 */
	public savePDF(pdfBlob: Blob, filename: string): void {
		const url = URL.createObjectURL(pdfBlob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename.endsWith('.pdf')
			? filename
			: `${filename}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}

	/**
	 * Generate text-only PDF from content with optional image inclusion
	 * @param textContent The text content to include in PDF
	 * @param title The title for the PDF
	 * @param filename The filename for the PDF
	 * @param sourceUrl Optional source URL to include in metadata
	 * @param includeImages Whether to include images from the HTML element (default: false)
	 * @param htmlElement Optional HTML element for image extraction when includeImages is true
	 * @returns Promise that resolves when PDF generation is complete
	 */
	public async generateTextOnlyPDF(
		textContent: string,
		title: string,
		filename: string,
		sourceUrl?: string,
		includeImages = false,
		htmlElement?: HTMLElement,
	): Promise<Blob> {
		try {
			const doc = new jsPDF('p', 'mm', 'a4');
			
			// Set document metadata
			doc.setProperties({
				title: title || filename,
				author: 'Meta Press PDF Service',
				creator: 'Meta Press PDF Service',
				subject: sourceUrl ? `Converted from: ${sourceUrl}` : 'Text content converted to PDF',
			});

			// PDF dimensions and margins
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const marginLeft = 20;
			const marginRight = 20;
			const marginTop = 20;
			const marginBottom = 20;
			const textWidth = pageWidth - marginLeft - marginRight;
			
			let currentY = marginTop;

			// Add title
			doc.setFontSize(16);
			doc.setFont('helvetica', 'bold');
			const titleLines = doc.splitTextToSize(title, textWidth);
			doc.text(titleLines, marginLeft, currentY);
			currentY += titleLines.length * 8 + 5;

			// Add source URL if provided
			if (sourceUrl) {
				doc.setFontSize(10);
				doc.setFont('helvetica', 'normal');
				doc.setTextColor(100, 100, 100);
				const urlText = `Source: ${sourceUrl}`;
				const urlLines = doc.splitTextToSize(urlText, textWidth);
				doc.text(urlLines, marginLeft, currentY);
				currentY += urlLines.length * 4 + 5;
				doc.setTextColor(0, 0, 0); // Reset to black
			}

			// Add creation date
			doc.setFontSize(10);
			doc.setFont('helvetica', 'normal');
			doc.setTextColor(100, 100, 100);
			const dateText = `Created: ${new Date().toLocaleString()}`;
			doc.text(dateText, marginLeft, currentY);
			currentY += 8;
			doc.setTextColor(0, 0, 0); // Reset to black

			// Add separator line
			currentY += 5;
			doc.setLineWidth(0.5);
			doc.line(marginLeft, currentY, pageWidth - marginRight, currentY);
			currentY += 10;

			// Add main content
			doc.setFontSize(11);
			doc.setFont('helvetica', 'normal');
			
			// Split content into paragraphs
			const paragraphs = textContent.split(/\n\s*\n/).filter(p => p.trim());
			
			for (const paragraph of paragraphs) {
				// Split paragraph into lines that fit the page width
				const lines = doc.splitTextToSize(paragraph.trim(), textWidth);
				
				for (const line of lines) {
					// Check if we need a new page
					if (currentY + 6 > pageHeight - marginBottom) {
						doc.addPage();
						currentY = marginTop;
					}
					
					doc.text(line, marginLeft, currentY);
					currentY += 5; // Line height
				}
				
				// Add spacing between paragraphs
				currentY += 3;
			}

			// Add images if requested and HTML element is provided
			if (includeImages && htmlElement) {
				currentY = await this.addImagesToPDF(doc, htmlElement, textWidth, marginLeft, marginBottom, pageHeight, currentY);
			}

			const pdfBlob = doc.output('blob');
			return pdfBlob;
		} catch (error) {
			console.error('Error generating text-only PDF:', error);
			throw error;
		}
	}

	/**
	 * Add images from HTML element to PDF document
	 * @param doc The jsPDF document to add images to
	 * @param htmlElement HTML element to extract images from
	 * @param textWidth Available width for content placement
	 * @param marginLeft Left margin for positioning
	 * @param marginBottom Bottom margin to avoid
	 * @param pageHeight Height of the page
	 * @param currentY Current Y position in the document
	 * @returns Updated Y position after adding images
	 */
	private async addImagesToPDF(
		doc: jsPDF,
		htmlElement: HTMLElement,
		textWidth: number,
		marginLeft: number,
		marginBottom: number,
		pageHeight: number,
		currentY: number,
	): Promise<number> {
		try {
			// Find all images in the HTML element
			const images = htmlElement.querySelectorAll('img');
			
			if (images.length === 0) {
				return currentY;
			}

			// Add images section header
			currentY += 10;
			if (currentY + 20 > pageHeight - marginBottom) {
				doc.addPage();
				currentY = 20;
			}
			
			doc.setFontSize(14);
			doc.setFont('helvetica', 'bold');
			doc.text('Images', marginLeft, currentY);
			currentY += 10;

			// Process each image
			for (let i = 0; i < images.length; i++) {
				const img = images[i] as HTMLImageElement;
				
				try {
					// Convert image to canvas to get image data
					const canvas = document.createElement('canvas');
					const ctx = canvas.getContext('2d');
					
					if (!ctx) {
						continue;
					}

					// Set canvas dimensions to match image
					canvas.width = img.naturalWidth || img.width;
					canvas.height = img.naturalHeight || img.height;
					
					// Draw image to canvas
					ctx.drawImage(img, 0, 0);
					
					// Convert canvas to data URL
					const dataURL = canvas.toDataURL('image/jpeg', 0.8);
					
					// Calculate image dimensions for PDF (maintain aspect ratio)
					const originalWidth = canvas.width;
					const originalHeight = canvas.height;
					const maxWidth = textWidth;
					const maxHeight = 100; // Max image height in mm
					
					let pdfWidth = maxWidth;
					let pdfHeight = (originalHeight / originalWidth) * pdfWidth;
					
					// Scale down if height exceeds maximum
					if (pdfHeight > maxHeight) {
						pdfHeight = maxHeight;
						pdfWidth = (originalWidth / originalHeight) * pdfHeight;
					}
					
					// Check if image fits on current page
					if (currentY + pdfHeight + 20 > pageHeight - marginBottom) {
						doc.addPage();
						currentY = 20;
					}
					
					// Add image to PDF
					doc.addImage(dataURL, 'JPEG', marginLeft, currentY, pdfWidth, pdfHeight);
					currentY += pdfHeight + 5;
					
					// Add image caption if alt text exists
					if (img.alt && img.alt.trim()) {
						doc.setFontSize(9);
						doc.setFont('helvetica', 'italic');
						doc.setTextColor(100, 100, 100);
						const captionLines = doc.splitTextToSize(img.alt, textWidth);
						doc.text(captionLines, marginLeft, currentY);
						currentY += captionLines.length * 4 + 5;
						doc.setTextColor(0, 0, 0); // Reset to black
					}
					
					currentY += 5; // Add spacing after image
					
				} catch (imageError) {
					console.warn(`Failed to process image ${i + 1}:`, imageError);
					// Continue with next image
				}
			}
			
			return currentY;
			
		} catch (error) {
			console.error('Error adding images to PDF:', error);
			return currentY;
		}
	}

	/**
	 * Generate PDF from HTML element
	 * @param element The HTML element to convert to PDF
	 * @param filename The filename for the PDF
	 * @returns Promise that resolves when PDF generation is complete
	 */
	public generatePDFFromElement(
		element: HTMLElement,
		_filename: string,
	): Promise<Blob> {
		return new Promise((resolve, reject) => {
			html2canvas(element, {
				scale: 2,
				useCORS: true,
				logging: false,
			})
				.then((canvas: any) => {
					try {
						const imgData = canvas.toDataURL('image/png');
						const pdf = new jsPDF({
							orientation: 'p',
							unit: 'mm',
							format: 'a4',
						});

						// Use canvas dimensions directly instead of getImageProperties
						const pdfWidth = pdf.internal.pageSize.getWidth();
						const pdfHeight =
							(canvas.height * pdfWidth) / canvas.width;

						pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

						const pdfBlob = pdf.output('blob');
						resolve(pdfBlob);
					} catch (error) {
						reject(error); // Reject if PDF generation fails
					}
				})
				.catch((error: any) => {
					reject(error); // Reject if html2canvas fails
				});
		});
	}
}

function GenerateMultiChoicePDF(
	exportData: Export,
	doc: jsPDF,
	yPosition: number,
): void {
	const gameData = exportData.gameData as MultiChoiceGameData;

	// Handle multi-choice game data
	if (gameData.questions && Array.isArray(gameData.questions)) {
		doc.setFontSize(16);
		doc.text('Multi-Choice Questions', 20, yPosition);
		yPosition += 10;

		gameData.questions.forEach((question: any, index: number) => {
			// Check if we need a new page
			if (yPosition > 270) {
				doc.addPage();
				yPosition = 20;
			}

			// Add question
			doc.setFontSize(12);
			doc.text(
				`Question ${index + 1}: ${question.questionText}`,
				20,
				yPosition,
			);
			yPosition += 7;

			// Add answers
			if (question.answers && Array.isArray(question.answers)) {
				question.answers.forEach((answer: any, ansIndex: number) => {
					// Check if we need a new page
					if (yPosition > 270) {
						doc.addPage();
						yPosition = 20;
					}

					const answerPrefix = answer.isCorrect ? '✓' : '○';
					doc.setFontSize(10);
					doc.text(`${answerPrefix} ${answer.text}`, 25, yPosition);
					yPosition += 5;
				});
			}

			// Add explanation if available
			if (question.explanationText) {
				// Check if we need a new page
				if (yPosition > 270) {
					doc.addPage();
					yPosition = 20;
				}

				doc.setFontSize(9);
				doc.setTextColor(100, 100, 100);
				doc.text(
					`Explanation: ${question.explanationText}`,
					25,
					yPosition,
				);
				doc.setTextColor(0, 0, 0);
				yPosition += 10;
			} else {
				yPosition += 5;
			}
		});
	}
	return;
}
